import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { projectTaskPath } from '../../../cross-team-shared/hub-paths';
import { RollbarService } from '../../../cross-team-shared/messaging/rollbar.service';
import { UpsertAnswerOperation } from '../../../team-apis/otterdam/annotation-operations/answer-operations';
import {
  UpdateMetadataOperation,
  UpdateMetadataOperationAttributes,
} from '../../../team-apis/otterdam/annotation-operations/metadata-operations';
import {
  DeleteObjectLocationOperation,
  DeleteObjectOperation,
  UpsertObjectLocationOperation,
  UpsertObjectOperation,
} from '../../../team-apis/otterdam/annotation-operations/object-operations';
import {
  DeleteShapeLocationOperation,
  DeleteShapeOperation,
  UpsertShapeLocationOperation,
  UpsertShapeOperation,
} from '../../../team-apis/otterdam/annotation-operations/shape-operations';
import { AnnotationEntityExternal } from '../../../team-apis/otterdam/annotations';
import { CalculateScoreRequest } from '../../../team-apis/otterdam/calculate-score-request';
import { CalculateScoreResponse } from '../../../team-apis/otterdam/calculate-score-response';
import { Task } from '../../../team-apis/otterdam/task';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { AbstractResource } from '../../http/abstract-resource';
import { buildDeleteObjectLocationOperation } from '../../http/persistence/draft/operation-builders/delete-object-location-operation-builder';
import { buildDeleteShapeLocationOperation } from '../../http/persistence/draft/operation-builders/delete-shape-location-operation-builder';
import { buildDeleteShapeOperation } from '../../http/persistence/draft/operation-builders/delete-shape-operation-builder';
import { FeatureFlagService } from '../misc-services/feature-flag.service';
import {
  isObjectLocationEntity,
  isShapeEntity,
  isShapeLocationEntity,
} from '../utils/annotation-external-utils';

export interface AnnotationShapeLocationRequestOptions {
  projectId: number;
  taskId: string;
  round: number;
  stepId: number;
}

export interface AnnotationRequestOptions {
  projectId: number;
  taskId: string;
  round: number;
  stepId?: number;
}

export type AnnotationOperation =
  | DeleteShapeOperation
  | UpsertShapeOperation
  | DeleteShapeLocationOperation
  | UpsertShapeLocationOperation
  | UpsertObjectOperation
  | DeleteObjectOperation
  | UpsertObjectLocationOperation
  | DeleteObjectLocationOperation
  | UpdateMetadataOperation
  | UpsertAnswerOperation;

const deleteShapeOrDeleteObjectOperation: Set<
  AnnotationOperation['operation']
> = new Set(['DELETE_SHAPE', 'DELETE_OBJECT']);

export const annotationQueueName = 'AnnotationPersistence';

@Injectable()
export class AnnotationResource extends AbstractResource {
  private store = inject(TaskWorkspaceStore);
  private rollbarService = inject(RollbarService);
  private featureFlagService = inject(FeatureFlagService);

  // eslint-disable-next-line max-lines-per-function -- temporary logging code
  uploadAnnotations$(batches: AnnotationOperation[]): Observable<unknown> {
    const request$ = this.request$<any>(
      'POST',
      annotationPath(this.getAnnotationsRequestOptions()),
      {
        body: batches,
        tokenScope: 'access_token',
        customErrorMessage: 'Failed to upload annotation.',
        responseType: 'json',
        timeout: 10_000,
      },
    );

    if (
      this.featureFlagService.hasFlag('log_delete_operations') &&
      batches.some((operation) =>
        deleteShapeOrDeleteObjectOperation.has(operation.operation),
      )
    ) {
      return request$.pipe(
        tap({
          next: (response: any) =>
            this.rollbarService.log('delete operation sent', batches, response),
          error: (err: unknown) =>
            typeof err === 'object' &&
            this.rollbarService.error(
              err || undefined,
              'delete operation sent',
              batches,
            ),
        }),
      );
    }

    return request$;
  }

  updateMetadata$(attributes: UpdateMetadataOperationAttributes) {
    const operation: UpdateMetadataOperation = {
      operation: 'UPDATE_METADATA',
      attributes,
    };
    return this.uploadAnnotations$([operation]);
  }

  getTrainingAnnotations$(task: Task) {
    const path = trainingAnnotationsPath(
      this.workspaceConfig.projectId,
      task.id,
    );
    return this.request$<AnnotationEntityExternal[]>('GET', path, {
      tokenScope: 'access_token',
      customErrorMessage: 'Failed to load training annotation.',
    });
  }

  calculateTrainingScore$(request: CalculateScoreRequest) {
    const path = trainingDiffPath(this.getAnnotationsRequestOptions());
    return this.request$<CalculateScoreResponse>('POST', path, {
      tokenScope: 'access_token',
      body: request,
      customErrorMessage: 'Error calculating training score.',
    });
  }

  // eslint-disable-next-line max-lines-per-function -- temporary until root cause of orphan entities is fixed
  deleteEntities$(entities: AnnotationEntityExternal[]) {
    const deleteShapeLocationOperations = entities
      .filter(isShapeLocationEntity)
      .map((location) =>
        buildDeleteShapeLocationOperation(
          location.frameNumber,
          location.shapeId,
          location.outputId,
        ),
      );
    const deleteObjectLocationOperations = entities
      .filter(isObjectLocationEntity)
      .map((location) =>
        buildDeleteObjectLocationOperation(
          location.objectId,
          location.frameNumber,
        ),
      );
    const deleteShapeOperations = entities
      .filter(isShapeEntity)
      .map((shape) => buildDeleteShapeOperation(shape.shapeId, shape.outputId));

    const batch = [
      deleteShapeLocationOperations,
      deleteObjectLocationOperations,
      deleteShapeOperations,
    ].flat();

    return this.request$(
      'POST',
      annotationPath(this.getAnnotationsRequestOptions()),
      {
        body: batch,
        tokenScope: 'access_token',
        customErrorMessage: 'Failed to upload annotation.',
      },
    );
  }

  private getAnnotationsRequestOptions() {
    const { project, task, stepId } = this.store.state();
    return {
      projectId: project.id,
      taskId: task.id,
      round: task.round,
      stepId: stepId!,
    };
  }
}

export function annotationAnswerPath({
  projectId,
  taskId,
  round,
  stepId,
}: AnnotationRequestOptions) {
  return `${annotationPath({ projectId, taskId, round, stepId })}/answers`;
}

export function annotationPath({
  projectId,
  taskId,
  round,
  stepId,
}: AnnotationRequestOptions) {
  return `${environment.annotationStoreServiceBaseUrl}${projectTaskPath(
    projectId,
    taskId,
  )}/rounds/${round}/steps/${stepId === undefined ? 'null' : stepId}`;
}

export function trainingAnnotationsPath(projectId: number, taskId: string) {
  return `${environment.annotationStoreServiceBaseUrl}${projectTaskPath(
    projectId,
    taskId,
  )}/gold`;
}

export function trainingDiffPath(requestOptions: AnnotationRequestOptions) {
  return `${annotationPath(requestOptions)}/training_diff`;
}
